<script setup lang="ts">
    import { computed, reactive, watch } from 'vue';
    import Modal from '@shared/components/Modal.vue';
    import FormRadioGroup from '@shared/components/Form/FormRadioGroup.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';
    import { deleteRole, UserAccountRole, type RoleSummary } from './Roles.access';

    const props = defineProps<{
        show: boolean;
        role: RoleSummary | null;
        roles: RoleSummary[];
    }>();

    const emit = defineEmits<{
        close: [];
        deleted: [];
    }>();

    const state = reactive<{
        selectedRoleValue: string | null;
        isDeleting: boolean;
    }>({
        selectedRoleValue: null,
        isDeleting: false,
    });

    watch(
        () => props.show,
        (show) => {
            if (!show) return;

            state.selectedRoleValue = null;
        }
    );

    const otherRoles = () => props.roles.filter((role) => role.RoleId !== props.role?.RoleId);

    const adminValue = String(UserAccountRole.Administrator);
    const userValue = String(UserAccountRole.User);

    const roleOptions = computed(() => [
        { label: $Resources.RoleDeleteAdminLabel, value: adminValue },
        { label: $Resources.RoleDeleteUserLabel, value: userValue },
        ...otherRoles().map((role) => ({ label: role.Name, value: role.RoleId })),
    ]);

    const replacementRoleType = computed<UserAccountRole | null>(() => {
        if (state.selectedRoleValue === null) return null;
        if (state.selectedRoleValue === adminValue) return UserAccountRole.Administrator;
        if (state.selectedRoleValue === userValue) return UserAccountRole.User;
        return UserAccountRole.Custom;
    });

    const replacementRoleId = computed<string | null>(() => (replacementRoleType.value === UserAccountRole.Custom ? state.selectedRoleValue : null));

    function close(): void {
        emit('close');
    }

    async function confirmDelete(): Promise<void> {
        if (state.isDeleting || replacementRoleType.value === null || !props.role) return;

        state.isDeleting = true;

        try {
            await deleteRole(props.role.RoleId, replacementRoleId.value, replacementRoleType.value);
            bsAlert({ type: AlertTypes.Success, content: $Resources.AlertRoleDeleteBody });
            emit('deleted');
        } catch {
            bsAlert({ type: AlertTypes.Danger });
        } finally {
            state.isDeleting = false;
        }
    }
</script>

<template>
    <Modal size="sm" :title="$Resources.RoleDeleteHeading" :show="props.show" @close="close">
        <template #content>
            <p>
                {{ $Resources.RoleDeleteText }}
                <strong>{{ props.role?.Name }}</strong>
            </p>

            <FormRadioGroup
                :legend="$Resources.RoleDeleteReplacementText"
                legendClasses="label-look m-sm-b"
                :options="roleOptions"
                :modelValue="state.selectedRoleValue"
                @update:modelValue="(value: string | null) => (state.selectedRoleValue = value)"></FormRadioGroup>
        </template>
        <template #button>
            <button
                class="btn btn-danger"
                :class="state.isDeleting && 'is-busy'"
                type="button"
                :disabled="replacementRoleType === null || state.isDeleting"
                @click="confirmDelete">
                {{ $Resources.RoleDeleteButtom }}
            </button>
        </template>
    </Modal>
</template>

